// # Admin Review Management Component
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../auth/state';
import { db } from '../../auth/firebase';
import { collection, query, getDocs, doc, runTransaction, updateDoc } from 'firebase/firestore';
import DashboardHeader from '../../components/DashboardHeader';
import DashboardFooter from '../../components/DashboardFooter';
import { formatProductName } from './user_review';
import './admin_review.css';
import cacheManager from '../../auth/cache_manager';

function AdminReview() {
  const navigate = useNavigate();
  const { isAdmin, user } = useAuthStore();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedReviews, setExpandedReviews] = useState(new Set());
  const [selectedReview, setSelectedReview] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [yearStats, setYearStats] = useState({});
  const [reportView, setReportView] = useState(null);
  const [activeTab, setActiveTab] = useState('all'); // 'all' or 'thisYear'
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'pending', 'accepted', 'rejected'
  const [isDarkMode, setIsDarkMode] = useState(false);

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, [user]);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (user && user.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', user.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }

        // # Dispatch theme change event for footer and other components
        const themeChangeEvent = new CustomEvent('theme_change', {
          detail: { isDark: newTheme }
        });
        window.dispatchEvent(themeChangeEvent);
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  useEffect(() => {
    loadReviews();
  }, []);

  const loadReviews = async () => {
    try {
      const reviewsRef = collection(db, 'reviews');
      const q = query(reviewsRef);
      const querySnapshot = await getDocs(q);
      
      let allReviews = [];
      const stats = {};
      
      querySnapshot.forEach((yearDoc) => {
        const yearData = yearDoc.data();
        // # Store year stats
        if (yearData.stats) {
          stats[yearDoc.id] = yearData.stats;
        }
        
        // # Process reviews from this year
        if (yearData.reviews) {
          const yearReviews = Object.entries(yearData.reviews).map(([reviewId, review]) => ({
            id: reviewId,
            year: yearDoc.id,
            ...review
          }));
          allReviews = [...allReviews, ...yearReviews];
        }
      });
      
      // # Sort reviews by timestamp, most recent first
      allReviews.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      
      setReviews(allReviews);
      setYearStats(stats);
    } catch (error) {
      console.error('Error loading reviews:', error);
      setError('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (reviewId, year, status) => {
    try {
      const yearDocRef = doc(db, 'reviews', year);
      
      await runTransaction(db, async (transaction) => {
        const yearDoc = await transaction.get(yearDocRef);
        if (!yearDoc.exists()) {
          throw new Error('Year document not found');
        }

        const yearData = yearDoc.data();
        const reviews = yearData.reviews || {};
        const stats = yearData.stats || {
          totalReviews: 0,
          pendingReviews: 0,
          acceptedReviews: 0,
          rejectedReviews: 0
        };

        // # Update review status
        if (reviews[reviewId]) {
          const oldStatus = reviews[reviewId].status;
          
          // # Skip if same status
          if (oldStatus === status) return;

          // # Update review data
          reviews[reviewId] = {
            ...reviews[reviewId],
            status,
            metadata: {
              ...reviews[reviewId].metadata,
              lastModified: new Date().toISOString()
            }
          };

          // # Update stats based on status transition
          // Decrement old status count
          if (oldStatus === 'pending') stats.pendingReviews--;
          else if (oldStatus === 'accepted') stats.acceptedReviews--;
          else if (oldStatus === 'rejected') stats.rejectedReviews--;

          // Increment new status count
          if (status === 'pending') stats.pendingReviews++;
          else if (status === 'accepted') stats.acceptedReviews++;
          else if (status === 'rejected') stats.rejectedReviews++;

          // # Update the document
          transaction.update(yearDocRef, {
            reviews,
            stats: {
              ...stats,
              lastUpdated: new Date().toISOString()
            }
          });
        }
      });

      // # Update local state
      setReviews(prevReviews => 
        prevReviews.map(review => 
          review.id === reviewId 
            ? { 
                ...review, 
                status,
                metadata: {
                  ...review.metadata,
                  lastModified: new Date().toISOString()
                }
              } 
            : review
        )
      );
      
    } catch (error) {
      console.error('Error updating review status:', error);
      setError('Failed to update review status');
    }
  };

  const handleDelete = async (reviewId, year) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;

    try {
      const yearDocRef = doc(db, 'reviews', year);
      
      await runTransaction(db, async (transaction) => {
        const yearDoc = await transaction.get(yearDocRef);
        if (!yearDoc.exists()) {
          throw new Error('Year document not found');
        }

        const yearData = yearDoc.data();
        const reviews = yearData.reviews || {};
        const stats = yearData.stats || {
          totalReviews: 0,
          pendingReviews: 0,
          acceptedReviews: 0,
          rejectedReviews: 0
        };

        // # Update stats based on the review being deleted
        if (reviews[reviewId]) {
          const status = reviews[reviewId].status;
          stats.totalReviews--;
          if (status === 'pending') stats.pendingReviews--;
          else if (status === 'accepted') stats.acceptedReviews--;
          else if (status === 'rejected') stats.rejectedReviews--;

          // # Delete the review
          delete reviews[reviewId];

          // # Update the document
          transaction.update(yearDocRef, {
            reviews,
            stats: {
              ...stats,
              lastUpdated: new Date().toISOString()
            }
          });
        }
      });

      // # Update local state
      setReviews(reviews.filter(review => review.id !== reviewId));
      
    } catch (error) {
      console.error('Error deleting review:', error);
      setError('Failed to delete review');
    }
  };

  // # Toggle review expansion
  const toggleReviewExpansion = (reviewId) => {
    setExpandedReviews(prev => {
      const newSet = new Set(prev);
      if (newSet.has(reviewId)) {
        newSet.delete(reviewId);
      } else {
        newSet.add(reviewId);
      }
      return newSet;
    });
  };

  const handleDeleteClick = (review) => {
    setDeleteConfirmation(review);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmation) return;
    
    try {
      await handleDelete(deleteConfirmation.id, deleteConfirmation.year);
      setDeleteConfirmation(null);
    } catch (error) {
      console.error('Error deleting review:', error);
      setError('Failed to delete review');
    }
  };

  const handleReadMore = (review) => {
    setReportView(review);
  };

  // # Get tab-specific stats (for dashboard)
  const getTabStats = () => {
    let tabReviews = reviews;
    if (activeTab === 'thisYear') {
      const currentYear = new Date().getFullYear().toString();
      tabReviews = reviews.filter(review => review.year === currentYear);
    }
    
    return {
      totalReviews: tabReviews.length,
      pendingReviews: tabReviews.filter(r => r.status === 'pending').length,
      acceptedReviews: tabReviews.filter(r => r.status === 'accepted').length,
      rejectedReviews: tabReviews.filter(r => r.status === 'rejected').length
    };
  };

  // # Filter reviews based on active tab and status
  const getFilteredReviews = () => {
    let filteredReviews = reviews;
    
    // Filter by year if needed
    if (activeTab === 'thisYear') {
      const currentYear = new Date().getFullYear().toString();
      filteredReviews = filteredReviews.filter(review => review.year === currentYear);
    }
    
    // Filter by status if needed
    if (statusFilter !== 'all') {
      filteredReviews = filteredReviews.filter(review => review.status === statusFilter);
    }
    
    return filteredReviews;
  };

  // # Tabs Component
  const renderTabs = () => {
    return (
      <div className="adm_rvw_tabs">
        <button 
          className={`adm_rvw_tab ${activeTab === 'all' ? 'adm_rvw_active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          All Reviews
        </button>
        <button 
          className={`adm_rvw_tab ${activeTab === 'thisYear' ? 'adm_rvw_active' : ''}`}
          onClick={() => setActiveTab('thisYear')}
        >
          This Year
        </button>
      </div>
    );
  };

  // # Status Filters Component
  const renderStatusFilters = () => {
    const stats = getTabStats(); // Get current tab stats for the counts
    
    return (
      <div className="adm_rvw_status-filters">
        <button 
          className={`adm_rvw_status-filter ${statusFilter === 'all' ? 'adm_rvw_active' : ''}`}
          onClick={() => setStatusFilter('all')}
        >
          All Status ({stats.totalReviews})
        </button>
        <button 
          className={`adm_rvw_status-filter ${statusFilter === 'pending' ? 'adm_rvw_active' : ''}`}
          onClick={() => setStatusFilter('pending')}
        >
          Pending ({stats.pendingReviews})
        </button>
        <button 
          className={`adm_rvw_status-filter ${statusFilter === 'accepted' ? 'adm_rvw_active' : ''}`}
          onClick={() => setStatusFilter('accepted')}
        >
          Accepted ({stats.acceptedReviews})
        </button>
        <button 
          className={`adm_rvw_status-filter ${statusFilter === 'rejected' ? 'adm_rvw_active' : ''}`}
          onClick={() => setStatusFilter('rejected')}
        >
          Rejected ({stats.rejectedReviews})
        </button>
      </div>
    );
  };

  // # Dashboard Stats Component
  const renderDashboard = () => {
    const stats = getTabStats();

    return (
      <div className="adm_rvw_dashboard">
        <h2 className="adm_rvw_dashboard-title">
          {activeTab === 'all' ? 'All Reviews Dashboard' : 'Current Year Dashboard'}
        </h2>
        <div className="adm_rvw_stats-grid">
          <div className="adm_rvw_stat-item">
            <span className="adm_rvw_stat-value">{stats.totalReviews}</span>
            <span className="adm_rvw_stat-label">Total Reviews</span>
          </div>
          <div className="adm_rvw_stat-item adm_rvw_pending">
            <span className="adm_rvw_stat-value">{stats.pendingReviews}</span>
            <span className="adm_rvw_stat-label">Pending</span>
          </div>
          <div className="adm_rvw_stat-item adm_rvw_accepted">
            <span className="adm_rvw_stat-value">{stats.acceptedReviews}</span>
            <span className="adm_rvw_stat-label">Accepted</span>
          </div>
          <div className="adm_rvw_stat-item adm_rvw_rejected">
            <span className="adm_rvw_stat-value">{stats.rejectedReviews}</span>
            <span className="adm_rvw_stat-label">Rejected</span>
          </div>
        </div>
      </div>
    );
  };

  // # Delete Confirmation Modal Component
  const DeleteConfirmationModal = ({ review, onConfirm, onCancel }) => (
    <div className="adm_rvw_confirm-modal-overlay">
      <div className="adm_rvw_confirm-modal">
        <div className="adm_rvw_confirm-icon">
          <span>!</span>
        </div>
        <h3 className="adm_rvw_confirm-title">Delete Review</h3>
        <div className="adm_rvw_confirm-content">
          <p className="adm_rvw_confirm-message">
            You are about to delete a review from <strong>{review.userName}</strong>
          </p>
          <div className="adm_rvw_confirm-details">
            <p>Product: <span>{formatProductName(review.product)}</span></p>
            <p>Status: <span className={`adm_rvw_status-${review.status}`}>{review.status.charAt(0).toUpperCase() + review.status.slice(1)}</span></p>
            <p>Date: <span>{new Date(review.timestamp).toLocaleDateString()}</span></p>
          </div>
          <p className="adm_rvw_confirm-warning">
            This action cannot be undone. The review will be permanently removed from the system.
          </p>
        </div>
        <div className="adm_rvw_confirm-actions">
          <button 
            className="adm_rvw_confirm-cancel" 
            onClick={onCancel}
          >
            Cancel
          </button>
          <button 
            className="adm_rvw_confirm-delete" 
            onClick={onConfirm}
          >
            Yes, Delete Review
          </button>
        </div>
      </div>
    </div>
  );

  // # Report View Modal Component
  const ReportViewModal = ({ review, onClose }) => (
    <div className="adm_rvw_confirm-modal-overlay" onClick={onClose}>
      <div className="adm_rvw_report-modal" onClick={e => e.stopPropagation()}>
        <button className="adm_rvw_report-close" onClick={onClose}>×</button>
        
        <div className="adm_rvw_report-header">
          <h2>{review.userName}'s Review</h2>
          <div className="adm_rvw_review-meta">
            <span className="adm_rvw_review-date">
              {new Date(review.timestamp).toLocaleDateString()}
            </span>
            <span className="adm_rvw_review-product">
              {formatProductName(review.product)}
            </span>
            <div className={`adm_rvw_review-status ${review.status ? 'adm_rvw_' + review.status : ''}`}>
              {review.status.charAt(0).toUpperCase() + review.status.slice(1)}
            </div>
          </div>
        </div>
        
        <div className="adm_rvw_report-content">
          <p className="adm_rvw_modal-text">{review.content}</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`adm_rvw_admin-page ${isDarkMode ? 'adm_rvw_dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      
      <div className="adm_rvw_admin-review-container">
        <h1>Review Management</h1>
        
        {renderTabs()}
        {renderDashboard()}
        {renderStatusFilters()}
        
        {loading ? (
          <div className="adm_rvw_loading">Loading reviews...</div>
        ) : error ? (
          <div className="adm_rvw_error">{error}</div>
        ) : (
          <div className="adm_rvw_reviews-grid">
            {getFilteredReviews().map((review) => (
              <div key={review.id} className={`adm_rvw_review-card ${review.status ? 'adm_rvw_' + review.status : ''}`}>
                <div className="adm_rvw_review-header">
                  <div className="adm_rvw_reviewer-info">
                    <h3>{review.userName}</h3>
                    <div className="adm_rvw_review-meta">
                      <span className="adm_rvw_review-date">
                        {new Date(review.timestamp).toLocaleDateString()}
                      </span>
                      <div className={`adm_rvw_review-status ${review.status ? 'adm_rvw_' + review.status : ''}`}>
                        {review.status.charAt(0).toUpperCase() + review.status.slice(1)}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="adm_rvw_review-product">
                  {formatProductName(review.product)}
                </div>

                <div className="adm_rvw_review-content">
                  <p>
                    {review.content.substring(0, 150)}
                    {review.content.length > 150 ? '...' : ''}
                  </p>
                  <button 
                    className="adm_rvw_read-more-button"
                    onClick={() => handleReadMore(review)}
                  >
                    Read More
                  </button>
                </div>
                
                <div className="adm_rvw_review-actions">
                  {review.status !== 'accepted' && (
                    <button
                      onClick={() => handleStatusUpdate(review.id, review.year, 'accepted')}
                      className="adm_rvw_accept-button"
                    >
                      {review.status === 'rejected' ? 'Re-accept' : 'Accept'}
                    </button>
                  )}
                  {review.status !== 'rejected' && (
                    <button
                      onClick={() => handleStatusUpdate(review.id, review.year, 'rejected')}
                      className="adm_rvw_reject-button"
                    >
                      {review.status === 'accepted' ? 'Re-reject' : 'Reject'}
                    </button>
                  )}
                  <button
                    onClick={() => handleDeleteClick(review)}
                    className="adm_rvw_delete-button"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {deleteConfirmation && (
        <DeleteConfirmationModal
          review={deleteConfirmation}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteConfirmation(null)}
        />
      )}

      {reportView && (
        <ReportViewModal
          review={reportView}
          onClose={() => setReportView(null)}
        />
      )}

      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
}

export default AdminReview;
